import { makeAutoObservable, runInAction } from "mobx";
import { inject, injectable } from "tsyringe";
import { TOKEN_IWrapTranslations, type IWrapTranslations } from "@/services/WrapTranslations";
import { TOKEN_IProjectBus, type IProjectBus } from "@/services/ProjectBus";
import { type IWrapTime, TOKEN_IWrapTime } from "@/services/WrapTime";
import RunningActionStore, { TOKEN_RunningActionStore } from "../utility-stores/RunningActionStore";
import { ProjectAccessStore, TOKEN_ProjectAccessStore } from "../utility-stores/ProjectAccessStore";
import { AccessLevel } from "@utilitydelta/eventplanedb-client";
import { TOKEN_IProjectEventGenerator, type IProjectEventGenerator } from "@/services/ProjectEventGenerator";

export const TOKEN_ShareLinkStore = Symbol("ShareLinkStore");

@injectable()
export class ShareLinkStore {
    private _shareLinkCopied: boolean = false;
    public get shareLinkCopied(): boolean {
        return this._shareLinkCopied;
    }
    private _accessLevel: AccessLevel = AccessLevel.Contributor;
    public get accessLevel(): AccessLevel {
        return this._accessLevel;
    }
    public set accessLevel(value: AccessLevel) {
        this._accessLevel = value;
    }

    private _isFork: boolean = false;
    public get isFork(): boolean {
        return this._isFork;
    }
    public set isFork(value: boolean) {
        this._isFork = value;
    }
    private _singleUse: boolean = true;
    public get singleUse(): boolean {
        return this._singleUse;
    }
    public set singleUse(value: boolean) {
        this._singleUse = value;
    }
    private _organisationDomain: string | undefined = undefined;
    public get organisationDomain(): string | undefined {
        return this._organisationDomain;
    }
    public set organisationDomain(value: string | undefined) {
        this._organisationDomain = value;
    }
    private _description: string | undefined = undefined;
    public get description(): string | undefined {
        return this._description;
    }
    public set description(value: string | undefined) {
        this._description = value;
    }
    private _expiresInHours: number = 1;
    public get expiresInHours(): number {
        return this._expiresInHours;
    }
    public set expiresInHours(value: number) {
        if (value < 0) {
            value = 0; // Ensure it doesn't go negative
        }
        this._expiresInHours = value;
    }
    private _shareUrl: string | undefined = undefined;
    public get shareUrl(): string | undefined {
        return this._shareUrl;
    }
    private _showMoreOptions: boolean = false;
    public get showMoreOptions(): boolean {
        return this._showMoreOptions;
    }


    constructor(
        @inject(TOKEN_RunningActionStore) private runningActionStore: RunningActionStore,
        @inject(TOKEN_IProjectEventGenerator) private projectEventGenerator: IProjectEventGenerator,
        @inject(TOKEN_IWrapTranslations) private t: IWrapTranslations,
        @inject(TOKEN_IProjectBus) projectBus: IProjectBus,
        @inject(TOKEN_IWrapTime) private wrapTime: IWrapTime,
        @inject(TOKEN_ProjectAccessStore) private readonly projectAccessStore: ProjectAccessStore,
    ) {
        makeAutoObservable(this)
        projectBus.subscribe_dialogStateChanged(async (data) => {
            if (!data.isOpen) {
                this.dialogClosedByUser();
            }
        });
    }

    viewOnlyLink = async () => {
        this.runningActionStore.manageAction(async () => {
            const oneHourExpiry = this.wrapTime.nowPlusHours(1);
            const result = await this.projectEventGenerator.addShareLink(
                this.projectAccessStore.currentProjectId!,
                false, AccessLevel.Viewer, false, undefined, oneHourExpiry);
            this._shareUrl = result;
        }, true, this.t.t("generating_view_only_link"));
    }

    contributorLink = async () => {
        this.runningActionStore.manageAction(async () => {
            const oneHourExpiry = this.wrapTime.nowPlusHours(1);
            const result = await this.projectEventGenerator.addShareLink(
                this.projectAccessStore.currentProjectId!,
                false, AccessLevel.Contributor, false, undefined, oneHourExpiry);
            this._shareUrl = result;
        }, true, this.t.t("generating_contributor_link"));
    }

    ownerLink = async () => {
        this.runningActionStore.manageAction(async () => {
            const oneHourExpiry = this.wrapTime.nowPlusHours(1);
            const result = await this.projectEventGenerator.addShareLink(
                this.projectAccessStore.currentProjectId!,
                false, AccessLevel.Owner, false, undefined, oneHourExpiry);
            this._shareUrl = result;
        }, true, this.t.t("generating_owner_link"));
    }

    generateMoreOptionsLink = async () => {
        this.runningActionStore.manageAction(async () => {
            const expiresOn = this._expiresInHours === 0 ? 0 : this.wrapTime.nowPlusHours(this._expiresInHours);
            const result = await this.projectEventGenerator.addShareLink(
                this.projectAccessStore.currentProjectId!,
                this._isFork, this._accessLevel, this._singleUse, this._description, expiresOn);
            this._shareUrl = result;
        }, true, this.t.t("generating_share_link"));
    }

    copyShareLinkToClipboard = async () => {
        if (!this._shareUrl) return;
        await navigator.clipboard.writeText(this._shareUrl!);
        this._shareLinkCopied = true;
        setTimeout(() => {
            runInAction(() => {
                this._shareLinkCopied = false;
            });
        }, 2000); // Reset after 2 seconds
    }

    generateAnotherLink = () => {
        this._shareUrl = undefined;
        this._description = undefined;
    }

    dialogClosedByUser = () => {
        this._shareUrl = undefined;
        this._isFork = false;
        this._accessLevel = AccessLevel.Contributor;
        this._singleUse = true;
        this._description = undefined;
        this._expiresInHours = 1;
        this._showMoreOptions = false;
    }

    userRequestedMoreOptions = () => {
        this._showMoreOptions = true;
    }
}